/**
 * URL of api endpoints
 *
 * This file should be served as a single truth for api endpoints urls
 */
export default {

  agents: {
    fetch: id => `agents/${id || ''}`,
    portraitPhoto: id => `agents/${id || ''}/portrait-photo`,
    landscapePhoto: id => `agents/${id || ''}/landscape-photo`,
    search: () => `agents`,
    claim: () => `agents/claim`,
    unclaim: () => `agents/unclaim`
  },

  attendance: {
    event: eventId => `attendance/${eventId || ''}`,
    list: eventId => `attendance/list/${eventId || ''}`,
    status: (eventId, userId, status) => `attendance/${eventId}/${userId}/${status}`,
    remove: (eventId, userId) => `attendance/${eventId}/${userId}`,
  },

  businessPlan: {
    showTracker: (userId) => `/business-plan/${userId}`,
    showEdit: (userId, year) => `/business-plan/edit/${userId || ''}/${year || ''}`,
    showTasks: (view, userId) => `/business-plan/tasks/${view || 'today'}/${userId || ''}`,
    setTemplate: (userId, year) => `/business-plan/edit/${userId}/${year}/template`,
    showTemplatePreview: (userId, year) => `/business-plan/edit/${userId}/${year}?preview=1`,
    createNextPlan: (userId) => `/business-plan/next/${userId || ''}`,
    showPlans: (userId) => `/business-plan/plans/${userId || ''}`,
    showReport: (userId, year) => `/business-plan/report/${userId}/${year}`,
    showActivity: (userId) => `/business-plan/activity/${userId || ''}`,
    showVideo: (videoId) => `/business-plan/prospect?video=${videoId}`,
    leaderboard: (view, userId, year) => `business-plan/leaderboard/${view}/${userId || ''}/${year || ''}`,
    showPrepwork: (userId, year) => `/business-plan/edit/prep/${userId}/${year || ''}`,
    prepwork: (userId, year) => `business-plan/prepwork/${userId}/${year || ''}`,
    approveLtm: (userId, year) => `business-plan/prepwork/${userId}/${year || ''}`,
    closingLeadSource: (closingId) => `business-plan/prepwork/closings/${closingId}`,
    ltmStats: (userId, year) => `business-plan/ltm-stats/${userId}/${year || ''}`,
    incomeGoal: (userId, year) => `business-plan/income-goal/${userId}/${year || ''}`,
    changeClosingConnection: (userId, year, connection) => `/business-plan/${userId}/${year}/closing-connection/${connection || ''}`,
    addClosing: (userId, year) => `/business-plan/closings/${userId}/${year || ''}`,
    deleteClosing: (userId, connection, id, year) => `/business-plan/closings/${userId}/${connection}/${id}?year=${year || ''}`,
    config: (userId, year) => `business-plan/${userId || ''}/${year || ''}`,
    rebuildTasks: (userId, year) => `business-plan/${userId}/${year}/tasks`,
    optOut: (userId, year) => `business-plan/opt-out/${userId || ''}/${year || ''}`,
    optIn: (userId, year) => `business-plan/opt-in/${userId || ''}/${year || ''}`,
    powerHour: () => `business-plan/power-hour`,
    engagements: (userId) => userId ? `business-plan/${userId}/engagements` : `business-plan/engagements`,
    copyIntoPlan: (userId, year) => `business-plan/copy-into-plan/${userId}/${year || ''}`,
    notCopiedInto: (userId, year) => `business-plan/not-copied-into/${userId}/${year}`,
    videoFeed: (userId, year) => `business-plan/videos/${userId || ''}/${year || '' }`,
    recentActivity: () => `business-plan/engagements/recent`
  },

  sites: {
    index: (userId) => `/sites/${userId || ''}`
  },

  calendars: {
    items: () => `calendars/items`
  },

  campaigns: {
    list: () => `campaigns`,
    acknowledge: (campaignRef) => `campaigns/${campaignRef}/ack`
  },

  callCenters: {
    fetch: id => `call-centers/${id}`,
    member: (callCenterId, callCenterMemberId) => `call-centers/${callCenterId}/members/${callCenterMemberId}`
  },

  contacts: {
    recruits: {
      register: () => `contacts/recruits`
    }
  },

  notes: {
    list: () => `notes`,
    edit: (note) => `notes/${note.id}`
  },

  cms: {
    embed: () => `cms/embed`
  },

  dashboard: {
    show: id => `/dashboard/${id || ''}`,
    list: userId => `dashboards/list/${userId || ''}`,
    widget: (id, tag) => `dashboards/widget/${id || ''}${tag ? '?tag=' + tag : ''}`,
    export: id => `dashboards/widget/${id ? id + '/export' : 'export'}`
  },

  files: {
    attachments: {
      list: () => `files/attachments`,
      show: id => `files/attachments/${id}`,
      scope: (id) => `files/attachments/${id}/scope`
    }
  },

  listings: {
    me: () => 'me/listings',
    show: (id) => `/listings/${id}`,
    search: () => `listings/search`,
    mediaArchive: (mlsConnection, mlsId) => `listings/${mlsConnection}/${mlsId}/media`,
    notifyMarketing: (id) => `listings/${id}/notify-marketing`,
    takenDownListing: (id) => `listings/${id}/taken-down`,
    reimbursement: (id) => `listings/${id}/reimbursement`,
  },

  notifications: {
    list: (userId) => userId ? `notifications/${userId}` : 'me/notifications'
  },

  offices: {
    roster: (id) => `offices/${id}/roster`
  },

  people: {
    show: (id) => `/people/${id}`
  },

  projects: {
    fetchByConnection: (connection, projectsId) => `projects/${connection}/${projectsId}`,
    fetch: (id) => `projects/${id}`
  },

  roster: {
    list: (type) => `rosters/${type || ''}`,
    search: () => `rosters/search`
  },

  reactions: {
    list: () => `me/reactions`,
    status: () => `me/reactions/status`,
    toggle: () => `me/reactions/toggle`
  },

  recruiting: {
    logActivity: field => `recruiting/activity/${field}`,
    contacts: {
      search: () => `recruiting/contacts/search`
    }
  },

  search: {
    query: () => `search`,
    config: () => `search/config`
  },

  settings: {
    calendars: (userId) => `/settings/calendars/${userId || ''}`,
    collaborations: (userId) => `/settings/collaborations/${userId || ''}`
  },

  support: {
    team: () => `me/support`,
    markGuideRead: (guideId) => `support/active-trail/guide/${guideId}/read`,
    toggleShowAgentExperience: () => `support/toggle-show-agent-experience`,
    search: () => `support/search`,
    ticket: () => `support/ticket`,
    topics: () => `support/topics`,
    showGuide: (id) => `support/guides/${id}`,
    showTopic: (id, type) => `/support/topics/${id}/${type || ''}`,
    favorites: (userId) => userId ? `support/favorites/${userId}` : `me/support/guides`,
    popular: (userId) => `support/popular/${userId || ''}`,
    showFavorites: (userId) => `/support/favorites/${userId || ''}`,
    showAllTopics: (userId) => `/support/topics/${userId || ''}`,
    guides: () => `support/guides`,
  },

  sso: {
    app: (id) => `sso/apps/${id}`,
    login: (app) => `/sso/${app.sso_connection}/login/${app.sso_id}`,
    edit: (app) => `/sso/${app.sso_connection}/login/${app.sso_id}/edit`,
    apps: () => `sso/apps`,
    me: () => `me/apps`,
  },

  tasks: {
    list: (view, userId) => userId ? `tasks/${view}/${userId}` : `me/tasks/${view || ''}`,
    edit: (task) => task ? `tasks/${task.id || ''}` : `me/tasks`,
    delete: (task) => `tasks/${task.id}`,
    complete: (task) => `tasks/${task.id}/complete`,
    count: (view, userId) => userId ? `tasks/${view}/${userId}/count` : `me/tasks/${view}/count`
  },

  testimonials: {
    list: (view) => `testimonials/${view || ''}`
  },

  upload: {
    document: () => `files/document`,
    image: () => `files/image`,
    fetch: () => `files/fetch`
  },

  users: {
    settings: (userId) => `/settings/${userId}`,
    accounts: (userId) => `users/${userId}/accounts`,
    addresses: {
      audit: (userId, addressId) => `users/${userId}/accounts/addresses/${addressId}`
    },
    impersonate: (userId) => `/settings/users/${userId}/impersonate`,
    docusign: (userId) => `users/${userId}/docusign`,
    verifyPassword: (userId) => userId ? `users/${userId}/verify-password` : `me/verify-password`,
    showForgotPassword: () => '/password/reset',
    saveTwoFactorAuthSecret: (userId) => userId ? `/settings/security/${userId}/2fa-secret` : '/settings/security/2fa-secret',
    removeTwoFactorAuth: (userId) => userId ? `/settings/security/${userId}/2fa-remove` : '/settings/security/2fa-remove',
    dismissTasksIcsModal: (userId) => userId ? `users/${userId}/dismiss/tasks-ics-modal` : `me/dismiss/tasks-ics-modal`,
    dismissPrepworkWelcomeModal: (userId) => userId ? `users/${userId}/dismiss/prepwork-welcome-modal`: `me/dismiss/prepwork-welcome-modal`,
    websites: {
      domain: (userId) => `users/${userId}/websites/domain`,
    },
  },

  videos: {
    stream: (type) => `videos/stream/${type || ''}`,
    embed: (type) => `videos/embed/${type || ''}`,
  }

}
